import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { mirrorAuthIdentity } from "@/lib/mirrorAuthSession";
import {
  importMicrosoftTodoListPage, loadMicrosoftTodoImportLists, MicrosoftTodoImportError,
  type MicrosoftTodoImportApplied, type MicrosoftTodoImportConnection, type MicrosoftTodoImportList,
} from "@/lib/microsoftTodoImport";
import { microsoftTodoImportCopy } from "@/lib/microsoftTodoImportCopy";
import ConnectionManageSurface from "@/components/connections/ConnectionManageSurface";
import { ConnectionBrandMark } from "@/components/connections/ConnectionBrandMark";

export interface MicrosoftTodoImportPanelProps {
  open: boolean;
  /** Pass the existing Microsoft flag/cohort gate; this panel does not create a grant. */
  enabled: boolean;
  ownerId: string | null;
  authSessionId: string | null;
  onClose: () => void;
  onImported: (page: MicrosoftTodoImportApplied) => void;
}
interface ActiveRequest { epoch: number; controller: AbortController; listId: string }
const buttonClass = "min-h-11 rounded-lg border border-border bg-background px-3 py-2.5 text-sm font-medium disabled:opacity-50";

export default function MicrosoftTodoImportPanel({
  open, enabled, ownerId, authSessionId, onClose, onImported,
}: MicrosoftTodoImportPanelProps) {
  const { lang } = useLanguage();
  const copy = microsoftTodoImportCopy[lang];
  const selectId = useId();
  const [lists, setLists] = useState<MicrosoftTodoImportList[]>([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [listCursor, setListCursor] = useState<string | null>(null);
  const [binding, setBinding] = useState<MicrosoftTodoImportConnection | null>(null);
  const [selected, setSelected] = useState("");
  const [page, setPage] = useState<MicrosoftTodoImportApplied | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<keyof typeof copy | null>(null);
  const epoch = useRef(0);
  const active = useRef<ActiveRequest | null>(null);
  const current = useRef({ open, enabled, ownerId, authSessionId, selected });
  current.current = { open, enabled, ownerId, authSessionId, selected };
  const invalidate = useCallback((update = true) => {
    epoch.current++;
    active.current?.controller.abort();
    active.current = null;
    if (update) setBusy(false);
  }, []);
  const clearLists = useCallback(() => {
    setLists([]); setListsLoaded(false); setListCursor(null); setBinding(null);
    setSelected(""); setPage(null);
  }, []);
  useEffect(() => {
    invalidate(); clearLists(); setError(null);
    return () => invalidate(false);
  }, [open, enabled, ownerId, authSessionId, invalidate, clearLists]);
  useEffect(() => {
    if (!open || !enabled || !ownerId || !authSessionId) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const identity = mirrorAuthIdentity(session);
      if (identity?.ownerId !== ownerId || identity.authSessionId !== authSessionId) {
        invalidate(); clearLists(); setError("sessionChanged");
      }
    });
    return () => subscription.unsubscribe();
  }, [open, enabled, ownerId, authSessionId, invalidate, clearLists]);

  const close = () => { invalidate(); onClose(); };
  const choose = (id: string) => {
    invalidate();
    current.current.selected = id;
    setSelected(id); setPage(null); setError(null);
  };
  const start = () => {
    if (active.current || !open || !enabled || !ownerId || !authSessionId) return null;
    const request: ActiveRequest = { epoch: ++epoch.current, controller: new AbortController(), listId: current.current.selected };
    active.current = request; setBusy(true); setError(null);
    const isCurrent = () => active.current === request && request.epoch === epoch.current &&
      !request.controller.signal.aborted && current.current.open && current.current.enabled &&
      current.current.ownerId === ownerId && current.current.authSessionId === authSessionId &&
      current.current.selected === request.listId;
    return { request, isCurrent, scope: { ownerId, authSessionId, signal: request.controller.signal, isCurrent } };
  };
  const failed = (cause: unknown) => {
    const code = cause instanceof MicrosoftTodoImportError ? cause.code : "import_failed";
    const key = ({
      invalid_session: "sessionChanged", import_cancelled: "cancelled",
      invalid_import_response: "invalidResponse", connection_changed: "connectionChanged",
      list_changed: "listChanged", import_expired: "expired", import_failed: "failed",
    } as const)[code];
    if (code === "connection_changed" || code === "list_changed" || code === "invalid_session") clearLists();
    setError(key);
  };
  const finish = (lease: NonNullable<ReturnType<typeof start>>) => {
    // An old finally cannot unlock a newer request after close or A -> B -> A.
    if (active.current === lease.request) { active.current = null; setBusy(false); }
  };
  const load = async (next = false) => {
    const continuation = next ? listCursor : null;
    if (next && (!continuation || !binding)) return;
    if (!next) {
      if (active.current) return;
      choose(""); clearLists();
    }
    const lease = start();
    if (!lease) return;
    try {
      const response = await loadMicrosoftTodoImportLists(lease.scope,
        continuation && binding ? { cursor: continuation, connection: binding } : {});
      if (!lease.isCurrent()) return;
      const combined = next ? [...lists, ...response.lists] : response.lists;
      const unique = new Map<string, MicrosoftTodoImportList>();
      for (const item of combined) {
        const prior = unique.get(item.id);
        if (prior && prior.title !== item.title) throw new MicrosoftTodoImportError("list_changed");
        unique.set(item.id, item);
      }
      setLists([...unique.values()]); setListsLoaded(true); setBinding(response.connection);
      setListCursor(response.next_cursor);
    } catch (cause) { if (lease.isCurrent()) failed(cause); }
    finally { finish(lease); }
  };
  const pull = async (next = false) => {
    const target = lists.find(item => item.id === selected);
    const continuation = next ? page?.next_cursor : null;
    if (!target || !binding || next && !continuation) return;
    const lease = start();
    if (!lease) return;
    try {
      const response = await importMicrosoftTodoListPage(lease.scope,
        { list: target, connection: binding, ...(continuation ? { cursor: continuation } : {}) });
      if (!lease.isCurrent()) return;
      setPage(response);
      // A parent refresh failure does not change an already verified import result.
      try { onImported(response); } catch { /* The confirmed page remains visible. */ }
    } catch (cause) { if (lease.isCurrent()) failed(cause); }
    finally { finish(lease); }
  };

  if (!open || !enabled || !ownerId || !authSessionId) return null;
  return <ConnectionManageSurface open={open} onOpenChange={next => { if (!next) close(); }}
    title={copy.title} description={copy.hint} closeLabel={copy.close}
    brand={<ConnectionBrandMark provider="microsoft_todo" />}>
    <div className="space-y-4 pt-4">
      <div className="flex flex-wrap gap-2">
        <button type="button" className={buttonClass} disabled={busy} onClick={() => { void load(); }}>{copy.loadLists}</button>
        {listCursor && <button type="button" className={buttonClass} disabled={busy} onClick={() => { void load(true); }}>{copy.moreLists}</button>}
      </div>
      {listsLoaded && lists.length === 0 && <p className="text-sm text-muted-foreground">{copy.emptyLists}</p>}
      {lists.length > 0 && <div className="space-y-2">
        <label htmlFor={selectId} className="block text-sm font-medium">{copy.chooseList}</label>
        <select id={selectId} value={selected} onChange={event => choose(event.target.value)}
          className="min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
          <option value="">{copy.chooseList}</option>
          {lists.map(item => <option key={item.id} value={item.id}>{item.title}</option>)}
        </select>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy || !selected} onClick={() => { void pull(); }}
            className={buttonClass + " border-primary/30 bg-primary/10 text-primary"}>
            {page ? copy.refresh : copy.importPage}
          </button>
          {page?.has_more && <button type="button" disabled={busy || !selected}
            className={buttonClass} onClick={() => { void pull(true); }}>{copy.moreTasks}</button>}
        </div>
      </div>}
      {busy && <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />{copy.working}
      </p>}
      {page && !busy && !error && <section aria-live="polite" className="space-y-3 rounded-xl border border-border p-3">
        <p role="status" className="font-medium">{page.result.status === "applied" ? copy.applied : copy.conflictStatus}</p>
        <dl className="space-y-1 text-sm">
          {(["inserted", "updated", "unchanged", "conflict", "tombstone", "pending"] as const).map(key =>
            <div key={key} className="flex justify-between gap-3"><dt>{copy[key]}</dt><dd>{page.result[key]}</dd></div>)}
        </dl>
        {page.scanned === 0 && <p className="text-sm text-muted-foreground">{copy.emptyPage}</p>}
        <p className="text-sm text-muted-foreground">{page.has_more ? copy.more : copy.end}</p>
        {page.replayed && <p className="text-sm text-muted-foreground">{copy.replayed}</p>}
      </section>}
      {error && <p role="alert" className="text-sm text-destructive">{copy[error]}</p>}
    </div>
  </ConnectionManageSurface>;
}
